import { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppraisalHeader from '../../../components/custom_components/Appraisal/AppraisalHeader'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import MeetingBackground from '../../../components/custom_components/Meeting/MeetingBackground'
import TabBar from '../../../components/custom_components/TabBar'
import { saveScoreGrid } from '../../../graphql/score-grid/hooks/useSaveScoreGrid'
import { FullMeetingFragment, Maybe, MeetingStepStatus, ScoreGoalDto, ScoreGridDto, ScoreGridInput } from '../../../types'
import TabSwitchingButtons from '../appraisal-flow/shared/TabSwitchingButtons'
import { getEvaluationStepMeetingStatus } from '../appraisal-flow/shared/util'
import { AppraisalContextType } from '../AppraisalTypes'
import './evaluation.less'
import { EvaluationContentComponent } from './EvaluationContentComponent'
import { EvaluationDescriptionComponent } from './EvaluationDescriptionComponent'
import { ScoreGridCategory } from './ScoreGridCategory.enum'
import { mapGoalsToInput, mapGoalsFromInitialState } from './EvaluationUtil'
import { isUserHrForCountry } from '../../../shared/rolesUtil'
import { useAuth } from '../../../context/auth-context'

interface Props {
  goalsState: ScoreGoalDto[]
  setGoalsState: (value: ScoreGoalDto[]) => void
  grid: ScoreGridDto
  meeting: FullMeetingFragment
  type: AppraisalContextType
  stepStatus: MeetingStepStatus
  onStatusChange: (value: MeetingStepStatus) => void
  isManagerEvaluation?: boolean
  showFooter: boolean
}

const EvaluationContentContainer: FC<Props> = ({
  grid,
  meeting,
  type,
  onStatusChange,
  stepStatus,
  goalsState,
  setGoalsState,
  showFooter,
}) => {
  const { t } = useTranslation()
  const [activeCategory, setActiveCategory] = useState(ScoreGridCategory.Customer)
  const { user } = useAuth()
  const isHrForCountry = isUserHrForCountry(meeting.appraisalSeason.country, user.roles)

  const evaluationVisible =
    meeting.employeePrepared &&
    (meeting.appraisal!.evaluationShared || (meeting.managerPrepared && meeting.selfReflectionAndEvaluationVisible) || isHrForCountry)

  const submitInputRef = useRef<{ grid: ScoreGridInput; meeting: FullMeetingFragment }>({
    grid: { ...grid, scoreGoals: mapGoalsFromInitialState(grid.scoreGoals, type, evaluationVisible) },
    meeting,
  })

  useEffect(() => {
    const status = getEvaluationStepMeetingStatus(goalsState, type)

    if (stepStatus !== status) {
      onStatusChange(status)
    }
  }, [goalsState, onStatusChange, type, stepStatus])

  useEffect(() => {
    const { meeting, grid } = submitInputRef.current
    return () => {
      saveScoreGrid(meeting.id, grid)
    }
  }, [])

  const goals = goalsState.filter((scoreGoal) => scoreGoal.viewId.includes(activeCategory))
  const tabs = Object.keys(ScoreGridCategory).map((entry) => {
    return { id: entry, title: t(`appraisalMeeting.evaluation.grid.category.${entry}`) }
  })

  const backgroundBoxId = 'evaluation-background-box'

  const handleTabClick = async (nextCategory: ScoreGridCategory) => {
    const backgroundBoxEl = document.getElementById(backgroundBoxId)
    setActiveCategory(ScoreGridCategory[nextCategory])
    if (backgroundBoxEl !== null) {
      backgroundBoxEl.scrollTop = 0
    }
  }

  const updateGridState = (newScoreGoal: ScoreGoalDto) => {
    let newScore: Maybe<number> | undefined
    if (type === AppraisalContextType.Employee) {
      newScore = newScoreGoal.employeeEvaluation
    } else if (type === AppraisalContextType.Manager) {
      newScore = newScoreGoal.reviewerEvaluation
    } else {
      newScore = newScoreGoal.resultEvaluation
    }

    if (newScore && newScore <= 10 && newScore >= 1) {
      const updatedGoals = goalsState.map((goal) => (goal.id === newScoreGoal.id ? newScoreGoal : goal))
      setGoalsState(updatedGoals)
      submitInputRef.current.grid.scoreGoals = mapGoalsToInput(updatedGoals)
    }
  }

  return (
    <BackgroundBox rt color="transparent" noPadding>
      <AppraisalHeader noShadow>
        <TabBar tabs={tabs} onTabClicked={handleTabClick} active={activeCategory} scoreGoals={goalsState} type={type} />
      </AppraisalHeader>

      <MeetingBackground id={backgroundBoxId} withFooter={showFooter} withHeader noShadow>
        <PerFlexRow direction="column" justify="space-between" className="per-evaluation-Content--fullHeight">
          <div>
            <EvaluationDescriptionComponent
              type={type}
              meeting={meeting}
              employeeStartedPreparing={goalsState.some((score) => !!score.employeeEvaluation)}
            />
            <EvaluationContentComponent type={type} goals={goals} update={updateGridState} meeting={meeting} />
          </div>
          <TabSwitchingButtons tabs={tabs} activeCategory={activeCategory} onChangeTabButtonClicked={handleTabClick} />
        </PerFlexRow>
      </MeetingBackground>
    </BackgroundBox>
  )
}

export default EvaluationContentContainer
